import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { ScanBarcode, Loader2, CheckCircle2, XCircle } from "lucide-react";

// Continuous camera barcode scanner. Uses the native BarcodeDetector when available,
// otherwise falls back to @zxing/browser. Calls onScan(code) for each unique read.
export default function BarcodeScanner({ open, onOpenChange, onScan, lastResult }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const nativeStop = useRef(null);
  const lastRef = useRef({ code: "", t: 0 });
  const [status, setStatus] = useState("");
  const [scanned, setScanned] = useState("");

  const emit = (code) => {
    const c = (code || "").replace(/\D/g, "");
    if (!c || c.length < 6) return;
    const now = Date.now();
    if (c === lastRef.current.code && now - lastRef.current.t < 1500) return;
    lastRef.current = { code: c, t: now };
    setScanned(c);
    if (navigator.vibrate) navigator.vibrate(60);
    onScan(c);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus("Avvio fotocamera…"); setScanned("");

    const startNative = async () => {
      // eslint-disable-next-line no-undef
      const detector = new window.BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
      const v = videoRef.current; v.srcObject = stream; await v.play();
      setStatus("");
      let running = true;
      nativeStop.current = () => { running = false; stream.getTracks().forEach((t) => t.stop()); };
      const loop = async () => {
        if (!running) return;
        try { const codes = await detector.detect(v); if (codes && codes[0]) emit(codes[0].rawValue); } catch { /* noop */ }
        if (running) setTimeout(loop, 350);
      };
      loop();
    };

    const startZxing = async () => {
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (result) emit(result.getText());
      });
      controlsRef.current = controls;
      setStatus("");
    };

    (async () => {
      try {
        if ("BarcodeDetector" in window) await startNative();
        else await startZxing();
      } catch (e) {
        setStatus("Impossibile accedere alla fotocamera. Consenti l'accesso o usa la ricerca manuale.");
      }
    })();

    return () => {
      cancelled = true;
      try { nativeStop.current && nativeStop.current(); } catch { /* noop */ }
      try { controlsRef.current && controlsRef.current.stop(); } catch { /* noop */ }
      const v = videoRef.current;
      if (v && v.srcObject) { v.srcObject.getTracks().forEach((t) => t.stop()); v.srcObject = null; }
      nativeStop.current = null; controlsRef.current = null;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[94vw]" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><ScanBarcode className="w-5 h-5 text-primary" /> Scansiona barcode</DialogTitle>
          <DialogDescription className="text-xs">Inquadra il codice a barre del prodotto: l'app cerca la riga corrispondente nel documento.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]" data-testid="barcode-video-wrap">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="barcode-video" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-3/4 h-16 border-2 border-white/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
          </div>
          {status && <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-white/90"><span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {status}</span></div>}
        </div>
        {scanned && <p className="text-xs text-center text-muted-foreground" data-testid="barcode-last-scanned">Ultimo codice: <span className="font-mono">{scanned}</span></p>}
        {lastResult && (
          <div className={`flex items-center gap-2 rounded-lg p-2 text-sm ${lastResult.ok ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-amber-500/15 text-amber-700"}`} data-testid="barcode-result">
            {lastResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {lastResult.msg}
          </div>
        )}
        <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="barcode-close-button">Chiudi</Button>
      </DialogContent>
    </Dialog>
  );
}
